import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAdminRole } from "@/hooks/useAdminRole";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { InsightsCoverageCard } from "@/components/admin/InsightsCoverageCard";
import type { AnyValue } from "@/lib/rowTypes";

const MODULES = [
  "budget_builder", "credit_management", "career_path",
  "entrepreneurship", "development", "scam_defense",
] as const;

interface ChildHealth {
  child_id: string;
  display_name: string | null;
  household_id: string | null;
  event_count_24h: number;
  event_count_7d: number;
  latest_event_at: string | null;
  snapshot_updated_at: string | null;
  rollup_updated_at: string | null;
  qa_event_count: number;
  failed_rollup_count: number;
  module_coverage: Record<string, { count: number; last_at: string | null }>;
  snapshot_pipeline: AnyValue[] | null;
}

export default function AdminDashboardDataHealth() {
  const { isAdmin, loading: roleLoading } = useAdminRole();
  const [rows, setRows] = useState<ChildHealth[]>([]);
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState<Record<string, AnyValue>[]>([]);

  const load = async () => {
    setLoading(true);
    const { data: children } = await supabase
      .from("child_profiles")
      .select("id, display_name, household_id")
      .limit(500);

    const now = Date.now();
    const out: ChildHealth[] = [];
    for (const c of children ?? []) {
      const [{ count: c24 }, { count: c7 }, latest, snap, rollup, qaCount, failedCount] = await Promise.all([
        supabase.from("learning_event_ledger").select("event_id", { count: "exact", head: true }).eq("child_id", c.id).gte("created_at", new Date(now - 86400000).toISOString()),
        supabase.from("learning_event_ledger").select("event_id", { count: "exact", head: true }).eq("child_id", c.id).gte("created_at", new Date(now - 7 * 86400000).toISOString()),
        supabase.from("learning_event_ledger").select("created_at").eq("child_id", c.id).order("created_at", { ascending: false }).limit(1).maybeSingle(),
        supabase.from("parent_dashboard_snapshots").select("updated_at, module_readiness_pipeline").eq("child_id", c.id).maybeSingle(),
        supabase.from("student_daily_learning_rollups").select("updated_at").eq("child_id", c.id).order("updated_at", { ascending: false }).limit(1).maybeSingle(),
        supabase.from("learning_event_ledger").select("event_id", { count: "exact", head: true }).eq("child_id", c.id).eq("is_qa", true),
        supabase.from("rollup_run_log").select("id", { count: "exact", head: true }).eq("child_id", c.id).eq("status", "error"),
      ]);

      // Per-module last_event + count
      const coverage: Record<string, { count: number; last_at: string | null }> = {};
      for (const m of MODULES) {
        const [{ count }, last] = await Promise.all([
          supabase.from("learning_event_ledger").select("event_id", { count: "exact", head: true }).eq("child_id", c.id).eq("module_slug", m).eq("is_qa", false),
          supabase.from("learning_event_ledger").select("created_at").eq("child_id", c.id).eq("module_slug", m).eq("is_qa", false).order("created_at", { ascending: false }).limit(1).maybeSingle(),
        ]);
        coverage[m] = { count: count ?? 0, last_at: (last.data)?.created_at ?? null };
      }

      out.push({
        child_id: c.id,
        display_name: c.display_name,
        household_id: c.household_id,
        event_count_24h: c24 ?? 0,
        event_count_7d: c7 ?? 0,
        latest_event_at: (latest.data)?.created_at ?? null,
        snapshot_updated_at: (snap.data)?.updated_at ?? null,
        rollup_updated_at: (rollup.data)?.updated_at ?? null,
        qa_event_count: qaCount.count ?? 0,
        failed_rollup_count: failedCount.count ?? 0,
        module_coverage: coverage,
        snapshot_pipeline: ((snap.data)?.module_readiness_pipeline ?? null) as AnyValue,
      });
    }
    setRows(out);

    const { data: runData } = await supabase
      .from("rollup_run_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    setRuns(runData ?? []);
    setLoading(false);
  };

  const repair = async (childId: string) => {
    const { error } = await supabase.functions.invoke("learning-rollup-repair", { body: { child_id: childId } });
    if (error) toast.error("Repair failed: " + error.message);
    else { toast.success("Repair invoked. Refreshing…"); await load(); }
  };

  useEffect(() => { if (isAdmin) void load(); }, [isAdmin]);

  if (roleLoading) return <div className="p-6 text-sm text-muted-foreground">Checking access…</div>;
  if (!isAdmin) return <div className="p-6 text-sm text-muted-foreground">Admins only.</div>;

  const fmt = (iso: string | null) => iso ? new Date(iso).toLocaleString() : "—";
  const stale = (iso: string | null, hours: number) => iso ? Date.now() - new Date(iso).getTime() > hours * 3600_000 : true;

  return (
    <div className="container max-w-5xl py-8 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard Data Health</h1>
          <p className="text-sm text-muted-foreground">Telemetry coverage, rollup freshness, and snapshot health across every child.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={async () => {
            const { error } = await supabase.functions.invoke("learning-rollup-repair", { body: {} });
            if (error) toast.error("Sweep failed: " + error.message); else toast.success("Sweep invoked");
            await load();
          }}>Repair stale</Button>
          <Button onClick={() => void load()} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</Button>
        </div>
      </div>

      <InsightsCoverageCard />

      <Card className="p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground">
            <tr><th className="text-left p-2">Child</th><th className="text-right p-2">24h</th><th className="text-right p-2">7d</th><th className="text-right p-2">QA</th><th className="text-right p-2">Failed</th><th className="text-left p-2">Latest event</th><th className="text-left p-2">Snapshot</th><th className="text-left p-2">State</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const flags: string[] = [];
              if (r.event_count_7d === 0) flags.push("No events");
              if (!r.rollup_updated_at) flags.push("No rollup");
              if (!r.snapshot_updated_at) flags.push("No snapshot");
              if (r.snapshot_updated_at && stale(r.snapshot_updated_at, 48)) flags.push("Stale snapshot");
              if (r.failed_rollup_count > 0) flags.push(`${r.failed_rollup_count} failed`);
              // Fallback warning: events exist but no module_readiness_pipeline entries.
              const hasModuleEvents = Object.values(r.module_coverage).some((m) => m.count > 0);
              const pipelineEmpty = !r.snapshot_pipeline || (r.snapshot_pipeline).length === 0;
              if (hasModuleEvents && pipelineEmpty) flags.push("Fallback in use");
              return (
                <React.Fragment key={r.child_id}>
                <tr className="border-t border-border/40">
                  <td className="p-2">{r.display_name ?? r.child_id.slice(0, 8)}</td>
                  <td className="p-2 text-right">{r.event_count_24h}</td>
                  <td className="p-2 text-right">{r.event_count_7d}</td>
                  <td className="p-2 text-right text-muted-foreground">{r.qa_event_count}</td>
                  <td className="p-2 text-right">{r.failed_rollup_count}</td>
                  <td className="p-2 text-xs text-muted-foreground">{fmt(r.latest_event_at)}</td>
                  <td className="p-2 text-xs text-muted-foreground">{fmt(r.snapshot_updated_at)}</td>
                  <td className="p-2">{flags.length === 0 ? <Badge variant="secondary">healthy</Badge> : <span className="flex flex-wrap gap-1">{flags.map((f) => <Badge key={f} variant="destructive" className="text-[10px]">{f}</Badge>)}</span>}</td>
                  <td className="p-2"><Button size="sm" variant="ghost" onClick={() => void repair(r.child_id)}>Repair</Button></td>
                </tr>
                <tr className="border-t border-border/20">
                  <td colSpan={9} className="p-2 pt-0">
                    <div className="flex flex-wrap gap-1.5">
                      {MODULES.map((m) => {
                        const cov = r.module_coverage[m];
                        return (
                          <Badge key={m} variant={cov.count > 0 ? "secondary" : "outline"} className="text-[10px] font-mono">
                            {m}: {cov.count}{cov.last_at ? ` · ${new Date(cov.last_at).toLocaleDateString()}` : ""}
                          </Badge>
                        );
                      })}
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Card className="p-4 overflow-x-auto">
        <h2 className="text-sm font-medium mb-2">Recent rollup runs</h2>
        <table className="w-full text-xs">
          <thead className="text-muted-foreground"><tr><th className="text-left p-1">When</th><th className="text-left p-1">Child</th><th className="text-left p-1">Status</th><th className="text-right p-1">Ms</th><th className="text-left p-1">Error</th></tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-t border-border/40">
                <td className="p-1">{fmt(r.created_at)}</td>
                <td className="p-1">{r.child_id?.slice(0, 8) ?? "—"}</td>
                <td className="p-1">{r.status}</td>
                <td className="p-1 text-right">{r.duration_ms ?? "—"}</td>
                <td className="p-1 text-muted-foreground">{r.error_message ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}